import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/archive';
import {
  Breadcrumb,
  byCategory,
  CategoryBadge,
  getCategory,
  MetaLine,
  PostCardVertical,
} from '@/components/feature/PostCard';
import { formatViews, tone } from '@/utils/chigua';

export default function ColumnPage() {
  const { slug = '' } = useParams();
  const cat = getCategory(slug);
  const list = byCategory(slug);
  const others = articles.filter((a) => a.category !== slug).slice(0, 3);

  useEffect(() => {
    if (cat) document.title = `${cat.name} - ${cat.tagline} | 每日吃瓜网`;
  }, [cat]);

  if (!cat) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
        <i className="ri-error-warning-line text-5xl text-primary-500" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-3xl text-foreground-950">找不到这个栏目</h1>
        <p className="mt-2 text-foreground-500">你访问的栏目可能已下线或链接有误。</p>
        <Link to="/" className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页吃瓜
        </Link>
      </div>
    );
  }

  const t = tone[cat.role];

  return (
    <div>
      {/* 栏目头图 */}
      <section className="relative overflow-hidden bg-foreground-950">
        <img src={cat.cover} alt={cat.name} title={`${cat.name}栏目头图`} className="absolute inset-0 h-full w-full object-cover object-top opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <Breadcrumb items={[{ name: '首页', to: '/' }, { name: cat.name }]} />
          <div className="flex flex-wrap items-center gap-4">
            <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${t.solid} shadow-sm`}>
              <i className={cat.icon} aria-hidden="true" />
            </span>
            <div>
              <h1 className="font-heading text-3xl text-background-50 md:text-4xl">{cat.name}</h1>
              <p className="mt-1 text-accent-300">{cat.tagline}</p>
            </div>
          </div>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">{cat.desc}</p>
        </div>
      </section>

      {/* 文章列表 */}
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                全部瓜报
                <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-sm font-normal text-foreground-600">{list.length}</span>
              </h2>
            </div>
            {list.length === 0 ? (
              <div className="rounded-3xl bg-background-100 p-10 text-center">
                <i className="ri-folder-unknow-line text-4xl text-foreground-400" aria-hidden="true" />
                <p className="mt-3 text-foreground-600">该栏目内容正在整理中，先去看看别的瓜吧。</p>
                <Link to="/" className="mt-4 inline-flex whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
                  返回首页 <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {list.map((a) => (
                  <article key={a.slug} className="group grid overflow-hidden rounded-2xl bg-background-100 sm:grid-cols-[280px_1fr]">
                    <Link to={`/news/${a.slug}`} className="relative block aspect-[16/9] overflow-hidden sm:aspect-auto sm:h-full">
                      <img src={a.hero} alt={a.title} title={a.title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </Link>
                    <div className="flex flex-col justify-center p-5 md:p-6">
                      <div className="mb-2 flex flex-wrap items-center gap-3">
                        <CategoryBadge slug={a.category} small />
                        <span className="text-xs text-foreground-500">{a.date}</span>
                      </div>
                      <h3 className="text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                        <Link to={`/news/${a.slug}`} className="line-clamp-2 transition-colors group-hover:text-primary-600">
                          {a.title}
                        </Link>
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.summary}</p>
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        <MetaLine a={a} />
                        <Link to={`/news/${a.slug}`} className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
                          吃这个瓜 <i className="ri-arrow-right-line" aria-hidden="true" />
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          {/* 侧栏 */}
          <aside className="space-y-5">
            <div className="rounded-3xl bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className={`ri-fire-line text-accent-500`} aria-hidden="true" /> 其他栏目热瓜
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                {others.map((a) => (
                  <PostCardVertical key={a.slug} a={a} />
                ))}
              </div>
            </div>
            <div className={`rounded-3xl ${t.soft} p-5`}>
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-user-heart-line text-primary-500" aria-hidden="true" /> 关于本栏目
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
              <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-foreground-700">
                <i className="ri-eye-line" aria-hidden="true" /> 累计热度
                <span className="font-bold text-primary-600">
                  {formatViews(list.reduce((s, a) => s + a.views, 0))}
                </span>
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}